<script setup>
import { Avatar } from '@dashhudson/dashing-ui/avatar';
import { Select } from '@dashhudson/dashing-ui/select';
import { SelectList } from '@dashhudson/dashing-ui/select-list';
import { computed, ref, watch } from 'vue';

import { MAX_RECENT_BRAND_USERS } from '@/app/community/constants';
import { ACTION_MENU_ITEMS } from '@/app/community/constants/interactionControl';
import { formatUser, getMostRecentUserIds, getMostRecentUsers } from '@/app/community/utils/teams';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useIdentityStore } from '@/stores/identity';
import { getUserInitials } from '@/utils/user';

const props = defineProps({
  showAvatar: { type: Boolean, default: undefined },
  modelValue: { type: Number, default: -1 },
  brandId: { type: Number, default: undefined },
  dropdown: { type: Object, default: undefined },
  applyCallback: { type: Function, default: () => {} },
});

const emit = defineEmits(['update:modelValue', 'commandName', 'valid']);

const identityStore = useIdentityStore();
const multiBrandStore = useCommunityMultiBrandsStore();

const internalValue = ref();
const internalShown = ref(false);

const component = computed(() => (props.dropdown ? Select : SelectList));
const menuProps = computed(() => {
  if (props.dropdown) {
    return {
      dropdown: props.showAvatar ? undefined : props.dropdown,
      hideControls: false,
      shown: internalShown.value,
      applyResetButtons: true,
      resetButtonValue: -1,
    };
  }
  return {};
});

const mostRecentAssignedUserIds = computed(() => {
  const recentAssignments = multiBrandStore.brandIdToAssignmentsMap[props.brandId];
  return getMostRecentUserIds({
    comments: recentAssignments,
    identity: identityStore.identity,
    limit: MAX_RECENT_BRAND_USERS,
  });
});
const mostRecentAssignedUsers = computed(() => {
  const accessibleUsers = multiBrandStore.brandIdToAccessibleBrandUsersMap[props.brandId] ?? [];
  return getMostRecentUsers({
    identity: identityStore.identity,
    mostRecentUserIds: mostRecentAssignedUserIds.value,
    accessibleUsers,
    addUnassigned: false,
  });
});
const brandAccessibleUsers = computed(() => {
  const accessibleUsers = multiBrandStore.brandIdToAccessibleBrandUsersMap[props.brandId] ?? [];
  const recentUserIds = [...mostRecentAssignedUserIds.value, identityStore.identity.id];
  return accessibleUsers
    .filter((user) => !recentUserIds.includes(user.id))
    .map((user) => formatUser(user));
});
const userOptions = computed(() => [
  ...mostRecentAssignedUsers.value,
  ...brandAccessibleUsers.value,
]);
const assigneeOptions = computed(() => {
  const options = [...userOptions.value];
  if (props.modelValue && props.modelValue !== identityStore.identity.id) {
    // Find the index and the user object
    const userIndex = options.findIndex((user) => user.value === props.modelValue);
    // If it exists, remove it and add it to the start of options list
    if (userIndex !== -1 && props.modelValue > 0) {
      const userObject = { ...options[userIndex] };
      options.splice(userIndex, 1);
      // index 2 to account for current user and the line
      options.splice(3, 0, userObject);
    }
  }
  return options;
});
const savedAssigneeValue = computed(() => (props.modelValue > 0 ? props.modelValue : -1));
const assigneeAvatar = computed(() => {
  if (props.showAvatar) {
    const user = assigneeOptions.value?.find((u) => u.value === props.modelValue) ?? {};
    return {
      image: user.avatarUrl,
      label: user.avatarUrl ? undefined : getUserInitials(user),
    };
  }
  return {};
});

async function onAssignUser(userId) {
  internalShown.value = false;
  if (props.applyCallback) {
    props.applyCallback(userId);
  }
}

function onAvatarClicked() {
  internalShown.value = !internalShown.value;
}

watch(
  () => internalValue.value,
  (to) => {
    if (to !== savedAssigneeValue.value) {
      emit('update:modelValue', to);
    }
    emit('valid', to !== savedAssigneeValue.value);
  },
);

watch(
  () => savedAssigneeValue.value,
  (to) => {
    internalValue.value = to;
    emit('commandName', 'Apply');
  },
  { immediate: true },
);
</script>

<template>
  <component
    :is="component"
    v-bind="menuProps"
    v-model="internalValue"
    :options="assigneeOptions"
    searchable
    search-placeholder="Search User"
    empty-text="No users found"
    :virtual-scroller="{ itemSize: 14, height: '13rem' }"
    @update:model-value="onAssignUser"
  >
    <template v-if="dropdown" #default>
      <Avatar
        v-if="showAvatar"
        v-dash-tooltip="ACTION_MENU_ITEMS.ASSIGN.label"
        v-bind="assigneeAvatar"
        size="sm"
        class="dash-cursor-pointer"
        @click.stop="onAvatarClicked"
      />
      <div v-else />
    </template>
    <template #optionLabel="{ label, option }">
      <div class="dash-inline-flex dash-items-center dash-gap-2">
        <Avatar
          v-if="option.value !== -1"
          size="xs"
          :image="option.avatarUrl"
          :label="option.avatarUrl ? null : getUserInitials(option)"
        />
        <span
          >{{ label }} <span v-if="option.note">{{ option.note }}</span></span
        >
      </div>
    </template>
  </component>
</template>
